import { useCallback, useEffect, useState } from 'react';
import { Alert, Box } from '@mui/material';
import type { DefinitionView, DepartmentView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { ProgressSection } from './ProgressSection';
import { ProtocolsSection } from './ProtocolsSection';
import { SessionsSection } from './SessionsSection';
import { TaskSection } from './TaskSection';

export interface AssessmentsPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  /** The permissions `/me` returned; the server enforces the same rules. */
  permissions: readonly string[];
  /** A session opened by a deep link such as `/assessments/{id}`. */
  initialSessionId?: string | undefined;
}

/**
 * The Assessments page. Coaches define protocols, run sessions and publish results; coaches,
 * guardians and athletes read published progress. Each part appears only for the people whose role
 * allows it, and the server applies the same rules.
 */
export function AssessmentsPanel({
  locale,
  organizationId,
  csrfToken,
  permissions,
  initialSessionId,
}: AssessmentsPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}`;
  const canManage = permissions.includes('assessments.manage');
  const canReadProgress = canManage || permissions.includes('results.own');
  const [departments, setDepartments] = useState<DepartmentView[]>([]);
  const [definitions, setDefinitions] = useState<DefinitionView[] | null>(null);
  const [errorKey, setErrorKey] = useState<ApiRequestError['messageKey'] | null>(null);

  const load = useCallback(async () => {
    if (!canManage) return;
    try {
      setDefinitions(await apiRequest<DefinitionView[]>(`${base}/assessment-definitions`));
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }, [base, canManage]);

  useEffect(() => {
    if (canManage) {
      apiRequest<DepartmentView[]>(`${base}/departments`).then(setDepartments, () => {
        setDepartments([]);
      });
    }
    void load();
  }, [base, canManage, load]);

  return (
    <Box sx={{ mt: 2 }}>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {canManage && (
        <>
          <TaskSection locale={locale} titleKey="assessments.protocols.title">
            <ProtocolsSection
              locale={locale}
              base={base}
              csrfToken={csrfToken}
              departments={departments}
              definitions={definitions}
              onChanged={load}
            />
          </TaskSection>
          <TaskSection locale={locale} titleKey="assessments.sessions.title" defaultOpen>
            <SessionsSection
              locale={locale}
              base={base}
              csrfToken={csrfToken}
              departments={departments}
              definitions={definitions}
              initialSessionId={initialSessionId}
            />
          </TaskSection>
        </>
      )}
      {canReadProgress && <ProgressSection locale={locale} base={base} />}
    </Box>
  );
}
